import { useEffect, useRef } from 'react';
import lottie, { type AnimationItem } from 'lottie-web';
import animationData from '@/assets/animations/mia-signature.json';

type MiaSignatureProps = {
  height?: number;
  color?: string;
  className?: string;
  loop?: boolean;
};

const NATIVE_ASPECT = 1240 / 536;

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  return [
    parseInt(full.slice(0, 2), 16) / 255,
    parseInt(full.slice(2, 4), 16) / 255,
    parseInt(full.slice(4, 6), 16) / 255,
  ];
}

function recolour(node: unknown, rgb: [number, number, number]): void {
  if (Array.isArray(node)) {
    for (const item of node) recolour(item, rgb);
    return;
  }
  if (node && typeof node === 'object') {
    const obj = node as Record<string, unknown>;
    const isFillOrStroke = obj.ty === 'fl' || obj.ty === 'st';
    if (isFillOrStroke && obj.c && typeof obj.c === 'object') {
      const colour = obj.c as { k?: unknown };
      if (Array.isArray(colour.k) && colour.k.every((n) => typeof n === 'number')) {
        const existing = colour.k as number[];
        colour.k =
          existing.length >= 4
            ? [rgb[0], rgb[1], rgb[2], existing[3]]
            : [rgb[0], rgb[1], rgb[2]];
      }
    }
    for (const key of Object.keys(obj)) {
      if (key !== 'c') recolour(obj[key], rgb);
    }
  }
}

function MiaSignature({
  height = 30,
  color = '#1a201d',
  className,
  loop = false,
}: MiaSignatureProps) {
  const container = useRef<HTMLDivElement | null>(null);
  const anim = useRef<AnimationItem | null>(null);

  useEffect(() => {
    const el = container.current;
    if (!el) return;

    const rgb = hexToRgb(color);
    const data = JSON.parse(JSON.stringify(animationData));
    recolour(data, rgb);

    anim.current = lottie.loadAnimation({
      container: el,
      renderer: 'svg',
      loop,
      autoplay: true,
      animationData: data,
      rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
    });

    return () => {
      anim.current?.destroy();
      anim.current = null;
    };
  }, [color, loop]);

  return (
    <div
      ref={container}
      className={className}
      style={{
        height,
        width: Math.round(height * NATIVE_ASPECT),
        lineHeight: 0,
        display: 'inline-block',
      }}
      aria-label='Mia'
      role='img'
    />
  );
}

export default MiaSignature;
